import { Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { AdditionalDriver } from '@/api/reservations/types';
import { Button, Card, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Person } from '@/ui/icons';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { TrashFill } from '@/ui/icons/trash-fill';

import {
  ADDITIONAL_DRIVER_STATUS_LABELS,
  additionalDriverStatus,
} from './status';

export function AdditionalDriverRow({
  driver,
  error,
  statusBelow = false,
  onPressInvite,
  onRemove,
}: {
  driver: Pick<AdditionalDriver, 'email' | 'status'> &
    Partial<Pick<AdditionalDriver, 'name'>>;
  error?: string;
  statusBelow?: boolean;
  onPressInvite?: () => void;
  onRemove?: () => void;
}) {
  const { styles, theme } = useStyles(stylesheet);
  const status = additionalDriverStatus(driver);
  const onPress = status === 'invite_sent' ? onPressInvite : undefined;
  const name = driver.name?.trim();
  const statusLabel = status ? (
    <Text type="caption1" style={styles[status]}>
      {ADDITIONAL_DRIVER_STATUS_LABELS[status]}
    </Text>
  ) : null;

  return (
    <Pressable disabled={!onPress} onPress={onPress} style={styles.row}>
      <Icon
        type="dressed"
        icon={<Person />}
        fillColor={theme.colors.icon.color}
        width={20}
        height={20}
        style={styles.icon}
      />

      <View style={styles.identity}>
        <Text type="bodyRegular">{name || driver.email}</Text>
        {name ? (
          <Text type="caption1" style={styles.secondary}>
            {driver.email}
          </Text>
        ) : null}
        {statusBelow ? statusLabel : null}
        {error ? (
          <Text type="caption1" style={styles.rejected}>
            {error}
          </Text>
        ) : null}
      </View>

      {statusBelow ? null : statusLabel}

      {onPress ? (
        <Icon
          type="naked"
          icon={<ChevronRight />}
          fillColor={theme.colors.icon.color}
          width={10}
          height={10}
        />
      ) : null}

      {onRemove ? (
        <Pressable onPress={onRemove} hitSlop={8}>
          <Icon
            type="dressed"
            icon={<TrashFill />}
            fillColor={theme.colors.icon.color}
            width={16}
            height={16}
          />
        </Pressable>
      ) : null}
    </Pressable>
  );
}

export function AdditionalDriversCard({
  drivers,
  tripStatus,
  onManage,
}: {
  drivers: AdditionalDriver[];
  tripStatus?: string;
  onManage: () => void;
}) {
  const { styles } = useStyles(stylesheet);
  // The API only accepts invites while the trip is in one of these states.
  const canInvite = ['open', 'verified', 'confirmed', 'rental'].includes(
    tripStatus ?? ''
  );

  return (
    <Card>
      <View style={styles.header}>
        <Text type="title2">Additional Drivers</Text>
        {canInvite ? (
          <Button
            text="+ Add"
            type="secondaryExtraSmall"
            onPress={onManage}
            style={styles.addButton}
            textStyle={styles.addButtonText}
          />
        ) : null}
      </View>

      {drivers.length > 0 ? (
        <View style={styles.list}>
          {drivers.map((driver) => (
            <AdditionalDriverRow key={driver.email} driver={driver} />
          ))}
        </View>
      ) : null}
    </Card>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  addButton: {
    height: 32,
    paddingTop: 0,
    paddingBottom: 0,
    borderRadius: 100,
  },
  addButtonText: {
    lineHeight: 16,
  },
  list: {
    padding: 16,
    gap: 16,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  icon: {
    width: 36,
    height: 36,
    borderRadius: 8,
  },
  identity: {
    flex: 1,
  },
  secondary: {
    color: theme.colors.text.secondary,
  },
  not_sent_yet: {
    color: theme.colors.text.secondary,
  },
  invite_sent: {
    color: theme.colors.chip.brand.color,
  },
  in_review: {
    color: theme.colors.pending,
  },
  verified: {
    color: theme.colors.success,
  },
  rejected: {
    color: theme.colors.error,
  },
}));
